import { FormCommonProps } from "src/components/common/forms/types";
import { DeepPartial } from "react-hook-form";
import { AavoForm, AavoFormContentParams } from "src/components/common/forms/AavoForm";
import { CatalogPartRevisionView } from "src/api/generated/erp/db/types/tables/catalogPartRevisionView";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField";
import { CatalogPartApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartApi";
import i18n from "i18next";
import { FetchAsyncOptionParams } from "src/components/common/inputFields/AsyncSelectField";
import { requireRule } from "src/components/common/forms/validation";
import { concatWithPipe } from "src/utils/strings";
import { getCatalogPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/catalogPartRevisionState";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox";
import { CopyCatalogPartRevisionContentFormApi } from "src/api/generated/erp/parts/catalogPart/api/copyCatalogPartRevisionContentFormApi";
import { FormLazySelectField } from "src/components/common/forms/fields/FormLazySelectField";
import { CatalogPartView } from "src/api/generated/erp/db/types/tables/catalogPartView";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CopyCatalogPartRevisionContentFormProps extends FormCommonProps<void> {
	targetCatalogPartRevision: CatalogPartRevisionView;
}

interface FormValues {
	sourceCatalogPartId: number;
	sourceRevisionId: number;
	copyBom: boolean;
	copyDocuments: boolean;
	copyConfigurator: boolean;
}

export const CopyCatalogPartRevisionContentForm = (props: CopyCatalogPartRevisionContentFormProps) => {
	const { targetCatalogPartRevision, onCompleted, onFormEdited } = props;
	return (
		<AavoForm
			defaultValues={getDefaultValues()}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.copyCatalogPartRevisionContent.form"),
				data: {
					targetCatalogPartId: targetCatalogPartRevision.catalogPartId,
					targetCatalogPartRevisionId: targetCatalogPartRevision.catalogPartRevisionId,
				},
			}}
			render={(contentParams) => <FormContent {...props} {...contentParams} />}
		/>
	);

	function getDefaultValues(): DeepPartial<FormValues> {
		return {
			sourceCatalogPartId: targetCatalogPartRevision.catalogPartId,
			copyBom: true,
			copyDocuments: true,
			copyConfigurator: targetCatalogPartRevision.isConfigurable,
		};
	}

	async function submit(values: FormValues) {
		await CopyCatalogPartRevisionContentFormApi.submit({
			targetPartRevisionId: targetCatalogPartRevision.catalogPartRevisionId,
			sourcePartRevisionId: values.sourceRevisionId,
			copyBom: values.copyBom,
			copyDocuments: values.copyDocuments,
			copyConfigurator: values.copyConfigurator,
		});
	}
};

interface FormContentProps extends CopyCatalogPartRevisionContentFormProps, AavoFormContentParams<FormValues> {}

const FormContent = ({ targetCatalogPartRevision, watch, control, setValue }: FormContentProps) => {
	const selectedSourceCatalogPartId = watch("sourceCatalogPartId");
	const isConfigurable = targetCatalogPartRevision.isConfigurable;

	return (
		<>
			<FormAsyncSelectField
				control={control}
				name={"sourceCatalogPartId"}
				label={i18n.t("from_part")}
				fetchOptions={({ searchQuery, currentSelection }: FetchAsyncOptionParams<number>) =>
					CatalogPartApi.getCatalogPartOptions({
						searchQuery: searchQuery,
						currentSelection: currentSelection,
					})
				}
				getOptionKey={(o: CatalogPartView) => o.catalogPartId}
				getOptionLabel={(o) => concatWithPipe(o.partNo, o.description_1, o.description_2)}
				rules={requireRule()}
				onChange={() => {
					setValue("sourceRevisionId", null);
				}}
			/>
			<FormLazySelectField
				control={control}
				name={"sourceRevisionId"}
				label={i18n.t("from_revision")}
				fetchOptions={async () => {
					if (!selectedSourceCatalogPartId) return [];
					return await CopyCatalogPartRevisionContentFormApi.getSourceRevisionOptions({
						sourceCatalogPartId: selectedSourceCatalogPartId,
						targetRevisionId: targetCatalogPartRevision.catalogPartRevisionId,
					});
				}}
				getOptionKey={(o: CatalogPartRevisionView) => o.catalogPartRevisionId}
				getOptionLabel={(o) => concatWithPipe(o.revisionNumber, getCatalogPartRevisionStateLabel(o.state))}
				rules={requireRule()}
			/>
			<FormCheckbox control={control} name={"copyBom"} label={i18n.t("copy_bom")} />
			<FormCheckbox control={control} name={"copyDocuments"} label={i18n.t("copy_documents")} />
			{isConfigurable && (
				<FormCheckbox
					control={control}
					name={"copyConfigurator"}
					label={i18n.t("copy_configurator_selections")}
				/>
			)}
		</>
	);
};
